import { environment } from '$env/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ToastrService } from 'ngx-toastr';
import { LanguageService } from './language.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { isUserAgreementNotAcceptedError } from '$core/models/user-agreement-not-accepted-error';
import { isMaintenance } from '$core/helpers/is-maintenance';
import {
  isChunkLoadError,
  isReportableHttpStatus,
  normalizeUrlPath,
} from '$core/helpers/sentry-filters';

/** Как у Sentry.createErrorHandler: ошибка дошла до ErrorHandler, никто её не обработал. */
const UNHANDLED_MECHANISM = { type: 'angular', handled: false };

@Injectable({
  providedIn: 'root',
})
export class ErrorsHandlerService extends ErrorHandler {
  constructor(private injector: Injector) {
    super();
  }

  handleError(error: Error | HttpErrorResponse) {
    const toastr = this.injector.get(ToastrService, null);
    const translateService = this.injector.get(TranslateService, null);

    while ((<any>error).rejection) {
      error = (<any>error).rejection;
    }

    // The user agreement updating modal is the only feedback for this error.
    if (isUserAgreementNotAcceptedError(error)) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      // Плановые техработы (503 + X-Maintenance): интерцептор уже показал
      // страницу техработ — ни тоста, ни события в Sentry.
      if (isMaintenance(error)) {
        return;
      }

      // Server or connection error happened
      if (!navigator.onLine) {
        // Handle offline error
        if (toastr) {
          toastr.info(translateService.instant('NO_INTERNET'));
        }
      } else {
        // Handle Http Error (error.status === 403, 404...)
        let title = 'AN_ERROR_HAS_OCCURRED';
        let text = 'TRY_AGAIN_LATER';

        switch (error.status) {
          case 413:
            title = 'SEND_ERROR';
            text = 'REDUCE_FILES_SIZE';
            break;

          case 429:
            title = 'TOO_MANY_REQUESTS';
            text = 'TRY_AGAIN_LATER';
            break;
        }

        if (toastr) {
          toastr.error(
            translateService.instant(text),
            translateService.instant(title)
          );
        }

        if (isReportableHttpStatus(error.status)) {
          this.reportHttpError(error);
        }
      }
    } else if (!isChunkLoadError(error)) {
      // Handle Client Error (Angular Error, ReferenceError...).
      // ChunkLoadError не отправляем: после деплоя страница перезагружается
      // (app.component.ts, NavigationError).
      this.report(error);
    }

    if (environment.showConsoleErrors) {
      console.error(error);
    }
  }

  private report(error: unknown): void {
    if (this.isSentryEnabled()) {
      Sentry.captureException(error, { mechanism: UNHANDLED_MECHANISM });
    }
  }

  private reportHttpError(error: HttpErrorResponse): void {
    if (!this.isSentryEnabled()) {
      return;
    }
    // HttpErrorResponse не наследует Error: без обёртки Sentry показывает
    // «Object captured as exception» без стека и сообщения.
    const httpError = new Error(error.message);
    httpError.name = 'HttpErrorResponse';
    Sentry.withScope((scope) => {
      // Без fingerprint все 5xx с одинаковым стеком склеились бы в один issue.
      scope.setFingerprint([
        'http-5xx',
        String(error.status),
        normalizeUrlPath(error.url),
      ]);
      scope.setContext('http', {
        url: error.url,
        status: error.status,
        statusText: error.statusText,
      });
      Sentry.captureException(httpError, { mechanism: UNHANDLED_MECHANISM });
    });
  }

  private isSentryEnabled(): boolean {
    return !!environment.sentry?.enabled && !window['webpackHotUpdate'];
  }

  public handleErrorCode(res: HttpErrorResponse) {
    if (isUserAgreementNotAcceptedError(res)) {
      return;
    }

    const router = this.injector.get(Router);
    const languageService = this.injector.get(LanguageService);

    if (!isMaintenance(res) && res.status === 503) {
      return;
    }

    router.navigate(
      [languageService.translateRoute(`/error/${res.status || 503}`)],
      { replaceUrl: true, skipLocationChange: true }
    );
  }

  public showErrorModal(error: HttpErrorResponse): void {
    if (isUserAgreementNotAcceptedError(error)) {
      return;
    }

    const res: HttpErrorResponse = error;
    const swal = this.injector.get(SwalService).swal;
    const translateService = this.injector.get(TranslateService);
    swal.icon = 'error';
    swal.title = translateService.instant('AN_ERROR_HAS_OCCURRED');
    if (res.status === 404) {
      swal.text = translateService.instant(res.error.message || 'NOT_FOUND');
      swal.fire();
    } else {
      let text = res.error
        ? res.error.message || res.error.error
        : res.statusText;

      if (res.error.errors && Object.keys(res.error.errors).length) {
        let arr = [];
        for (const [key, value] of Object.entries(res.error.errors)) {
          arr = arr.concat(value);
        }

        if (arr && arr.length) {
          if (arr.length === 1) {
            text = arr[0];
          } else {
            text = `<ul class="list-dotted list-dotted--spaced text-left">
          ${arr.reduce((str, el) => {
            return '<li>' + el + '</li>';
          }, '')}
          </ul>`;
          }
        }
      }

      swal.html = text;
      swal.fire();
    }
  }
}
